import React, { useEffect, useState } from "react";
import { Box, InputAdornment, Tab, Tabs, TextField } from "@mui/material";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as WarningIcon } from "assets/images/icons/icn_btn_authenticate_red.svg";
import iconClose from "assets/images/icons/icn_close.svg";
import iconSwitch from "assets/images/icons/img_device_switch.svg";
import { SolidButton } from "components/buttons/SolidButton";
import { SolidButton as SolidRectangleButton } from "components/buttons/SolidRectangleButton";
import { authStateOption } from "../../const";
import { getDefaultTabProp, getDefaultTextFieldProp } from "../../theme";
import mainStyle from "../../mainStyle.module.scss";
import drawerStyle from "./deviceDrawer.module.scss";

const DeviceDrawer = ({
  open,
  device,
  username,
  setUsername,
  password,
  setPassword,
  onClose,
  onAuthenticate,
  onAdopt,
}) => {
  const [openForm, setOpenForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authAttempted, setAuthAttempted] = useState(false);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    setOpenForm(false);
    setShowPassword(false);
    setAuthAttempted(false);
    setAuthError(false);
  }, [device?.id]);

  useEffect(() => {
    if (!authAttempted || !device) return;
    if (device.authStatus === authStateOption.passed) {
      setAuthError(false);
      setOpenForm(false);
    } else if (device.authStatus === authStateOption.failed) {
      setAuthError(true);
    }
  }, [authAttempted, device?.authStatus, device]);

  if (!device) return null;

  const submitAuthentication = () => {
    if (!username.trim() || !password || device.authStatus == null) return;
    setAuthAttempted(true);
    setAuthError(false);
    onAuthenticate();
  };

  const authPassed = device.authStatus === authStateOption.passed;
  const authenticating = authAttempted && device.authStatus == null;

  return (
    <Box
      className={`${drawerStyle["drawer-info"]} ${open ? drawerStyle.open : ""}`}
      role="complementary"
      aria-hidden={!open}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <img src={iconSwitch} alt="Switch" />
          <Box className={drawerStyle["model-name-container"]}>
            <Box sx={{ ml: "10px", color: "#373737", fontWeight: "bold", fontSize: "20px" }}>
              {device.modelName || "Data Center Switch"}
            </Box>
            <Box display="flex" alignItems="center">
              <Box className={`${mainStyle["chip-name"]} ${mainStyle[`${device.status.value}-chip`]} ${drawerStyle["status-chip"]}`}>
                {device.status.label}
              </Box>
            </Box>
          </Box>
        </Box>
        <img src={iconClose} alt="Close" style={{ cursor: "pointer" }} onClick={onClose} />
      </Box>

      <Box>
        <Tabs
          value="overview"
          TabIndicatorProps={{ ...getDefaultTabProp().tabIndicatorProps }}
          sx={{ ...getDefaultTabProp().sx }}
        >
          <Tab value="overview" label="Overview" />
        </Tabs>
      </Box>

      <Box sx={{ p: "16px 8px" }}>
        <Box className={drawerStyle["field-container"]}>
          <Box className={drawerStyle.field}>Auth</Box>
          <Box className={drawerStyle.value}>
            {authPassed ? (
              <Box className={drawerStyle.success}>Configurable</Box>
            ) : authenticating ? (
              <Box>Authenticating...</Box>
            ) : (
              <Box className={drawerStyle.auth}>
                {!openForm && (
                  <Box className={`${drawerStyle.btn} ${drawerStyle.warning}`} onClick={() => setOpenForm(true)}>
                    <WarningIcon />
                    Authentication Required
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {openForm && !authPassed && (
            <>
              <Box className={drawerStyle.field} sx={{ justifyContent: "end" }}><li>Username</li></Box>
              <Box className={drawerStyle.value}>
                <TextField
                  fullWidth
                  value={username}
                  autoComplete="new-password"
                  onChange={(event) => setUsername(event.target.value)}
                  sx={{ ...getDefaultTextFieldProp().sx }}
                />
              </Box>
              <Box className={drawerStyle.field} sx={{ justifyContent: "end" }}><li>Password</li></Box>
              <Box className={drawerStyle.value}>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  value={password}
                  autoComplete="new-password"
                  onChange={(event) => setPassword(event.target.value)}
                  sx={{ ...getDefaultTextFieldProp().sx }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {showPassword ? (
                          <ShowIcon onClick={() => setShowPassword(false)} style={{ cursor: "pointer" }} />
                        ) : (
                          <HideIcon onClick={() => setShowPassword(true)} style={{ cursor: "pointer" }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {authError && (
                <>
                  <Box className={drawerStyle.field} />
                  <Box className={drawerStyle.value}><Box className={drawerStyle.error}>Invalid username or password</Box></Box>
                </>
              )}
              <Box className={drawerStyle.field} />
              <Box className={drawerStyle.value}>
                <SolidRectangleButton
                  className={`${drawerStyle["submit-btn"]} ${username.trim() && password && !authenticating ? drawerStyle["enabled-btn"] : drawerStyle["disabled-btn"]}`}
                  onClick={submitAuthentication}
                >
                  {authenticating ? "Authenticating..." : "Submit"}
                </SolidRectangleButton>
              </Box>
            </>
          )}

          <Box className={drawerStyle.field}>IP Address</Box>
          <Box className={drawerStyle.value}>{device.ipAddress || "-"}</Box>
          <Box className={drawerStyle.field}>MAC Address</Box>
          <Box className={drawerStyle.value}>{device.macAddress || "-"}</Box>
          <Box className={drawerStyle.field}>Model Name</Box>
          <Box className={drawerStyle.value}>{device.modelName || "-"}</Box>
          <Box className={drawerStyle.field}>Firmware Version</Box>
          <Box className={drawerStyle.value}>{device.version || "-"}</Box>
          <Box className={drawerStyle.field}>Hardware Version</Box>
          <Box className={drawerStyle.value}>{device.hardwareVersion || "-"}</Box>
          <Box className={drawerStyle.field}>Spine/Leaf Role</Box>
          <Box className={drawerStyle.value}>{device.role || "Spine"}</Box>
        </Box>
        {authPassed && !device.provisioning && (
          <Box className={drawerStyle["button-container"]}>
            <SolidButton
              className={`${drawerStyle["apply-btn"]} ${drawerStyle["enabled-btn"]}`}
              onClick={onAdopt}
            >
              <span>Adopt</span>
            </SolidButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DeviceDrawer;
